import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPredefinedPlayoffs,
  cleanUpSchedules,
  fetchSeasonById,
  fetchSeasonRules,
  fetchSeasonWorkflowEvents,
  finalizeSchedule,
  generateSchedulePreview,
  progressSeasonPlayoffs,
  seedPlayoffsFromStandings,
  transitionSeasonPhase,
  updateSeasonRules,
} from '../api';

const initialRules = {
  workflow_phase: 'DRAFT',
  is_locked: false,
  regular_num_legs: 2,
  points_for_win: 3,
  points_for_loss: 0,
  tiebreakers: ['points', 'wins', 'set_difference', 'sets_won'],
  playoff_enabled: true,
  playoff_format: 'TWO_LEG_KNOCKOUT',
  playoff_teams_count: 8,
  auto_seed_playoffs: true,
  regular_season_start: '',
  regular_season_end: '',
  playoffs_start: '',
  playoffs_end: '',
  metadata: {},
};

const PHASE_ACTIONS = [
  { label: 'Przejdź do Regular Season', to_phase: 'REGULAR_SEASON' },
  { label: 'Przejdź do Playoffs', to_phase: 'PLAYOFFS' },
  { label: 'Zakończ sezon', to_phase: 'COMPLETED' },
  { label: 'Archiwizuj', to_phase: 'ARCHIVED' },
];

const WORKFLOW_STEPS = ['DRAFT', 'REGULAR_SEASON', 'PLAYOFFS', 'COMPLETED', 'ARCHIVED'];
const WORKFLOW_STEP_LABELS = {
  DRAFT: 'Draft',
  REGULAR_SEASON: 'Regular Season',
  PLAYOFFS: 'Playoffs',
  COMPLETED: 'Zakończony',
  ARCHIVED: 'Archiwum',
};

const ALLOWED_TRANSITIONS = {
  DRAFT: ['REGULAR_SEASON', 'ARCHIVED'],
  REGULAR_SEASON: ['PLAYOFFS', 'COMPLETED', 'ARCHIVED'],
  PLAYOFFS: ['COMPLETED', 'ARCHIVED'],
  COMPLETED: ['ARCHIVED'],
  ARCHIVED: [],
};

const defaultMetadataText = JSON.stringify({}, null, 2);

const SeasonRulesPage = () => {
  const { seasonId } = useParams();
  const [rules, setRules] = useState(initialRules);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tiebreakersText, setTiebreakersText] = useState(initialRules.tiebreakers.join(', '));
  const [metadataText, setMetadataText] = useState(defaultMetadataText);
  const [predefinedStartDate, setPredefinedStartDate] = useState('');

  const validateRulesPayload = () => {
    const toInt = (value, fallback) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const regularNumLegs = toInt(rules.regular_num_legs, initialRules.regular_num_legs);
    const pointsForWin = toInt(rules.points_for_win, initialRules.points_for_win);
    const pointsForLoss = toInt(rules.points_for_loss, initialRules.points_for_loss);
    const playoffTeamsCount = toInt(rules.playoff_teams_count, initialRules.playoff_teams_count);

    if (regularNumLegs < 1) {
      return { errorMessage: 'Liczba rund (legs) musi być >= 1.' };
    }
    if (pointsForWin < pointsForLoss) {
      return { errorMessage: 'Punkty za wygraną nie mogą być mniejsze od punktów za porażkę.' };
    }
    if (![2, 4, 8].includes(playoffTeamsCount)) {
      return { errorMessage: 'Liczba drużyn playoff musi wynosić 2, 4 lub 8.' };
    }

    const regularSeasonStart = rules.regular_season_start || null;
    const regularSeasonEnd = rules.regular_season_end || null;
    const playoffsStart = rules.playoffs_start || null;
    const playoffsEnd = rules.playoffs_end || null;

    if (regularSeasonStart && regularSeasonEnd && regularSeasonEnd < regularSeasonStart) {
      return { errorMessage: 'Koniec regular season nie może być wcześniejszy niż start.' };
    }
    if (playoffsStart && playoffsEnd && playoffsEnd < playoffsStart) {
      return { errorMessage: 'Koniec playoff nie może być wcześniejszy niż start.' };
    }
    if (regularSeasonEnd && playoffsStart && playoffsStart < regularSeasonEnd) {
      return { errorMessage: 'Start playoff nie może być wcześniejszy niż koniec regular season.' };
    }

    const tiebreakers = tiebreakersText
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (tiebreakers.length === 0) {
      return { errorMessage: 'Podaj przynajmniej jeden tiebreaker.' };
    }

    let metadata = {};
    try {
      metadata = metadataText?.trim() ? JSON.parse(metadataText) : {};
    } catch {
      return { errorMessage: 'Metadata musi być poprawnym JSON.' };
    }

    return {
      payload: {
        is_locked: !!rules.is_locked,
        regular_num_legs: regularNumLegs,
        points_for_win: pointsForWin,
        points_for_loss: pointsForLoss,
        tiebreakers,
        playoff_enabled: !!rules.playoff_enabled,
        playoff_format: rules.playoff_format || initialRules.playoff_format,
        playoff_teams_count: playoffTeamsCount,
        auto_seed_playoffs: !!rules.auto_seed_playoffs,
        regular_season_start: regularSeasonStart,
        regular_season_end: regularSeasonEnd,
        playoffs_start: playoffsStart,
        playoffs_end: playoffsEnd,
        metadata,
      },
    };
  };

  const withAction = async (actionName, run) => {
    setBusyAction(actionName);
    setMessage('');
    setError('');
    try {
      await run();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Operacja nie powiodła się.');
    } finally {
      setBusyAction('');
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [rulesResponse, eventsResponse] = await Promise.all([
        fetchSeasonRules(seasonId),
        fetchSeasonWorkflowEvents(seasonId),
      ]);

      const loadedRules = rulesResponse.data || initialRules;
      const normalizedRules = {
        ...initialRules,
        ...loadedRules,
        regular_season_start: loadedRules.regular_season_start || '',
        regular_season_end: loadedRules.regular_season_end || '',
        playoffs_start: loadedRules.playoffs_start || '',
        playoffs_end: loadedRules.playoffs_end || '',
      };

      setRules(normalizedRules);
      setTiebreakersText(
        Array.isArray(normalizedRules.tiebreakers)
          ? normalizedRules.tiebreakers.join(', ')
          : initialRules.tiebreakers.join(', ')
      );
      setMetadataText(JSON.stringify(normalizedRules.metadata || {}, null, 2));
      setPredefinedStartDate(normalizedRules.playoffs_start || '');
      setEvents(Array.isArray(eventsResponse.data) ? eventsResponse.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Nie udało się pobrać reguł sezonu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId]);

  const handleChange = (field, value) => {
    setRules((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    await withAction('save', async () => {
      const { payload, errorMessage } = validateRulesPayload();
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      await updateSeasonRules(seasonId, payload);
      setMessage('Reguły sezonu zostały zapisane.');
      await loadData();
    });
  };

  const handleTransition = async (toPhase) => {
    await withAction(`transition-${toPhase}`, async () => {
      await transitionSeasonPhase(seasonId, {
        to_phase: toPhase,
        note: `Manual transition from CMS to ${toPhase}`,
      });
      setMessage(`Zmieniono fazę sezonu na ${toPhase}.`);
      await loadData();
    });
  };

  const handleSeedPlayoffs = async () => {
    await withAction('seed', async () => {
      const response = await seedPlayoffsFromStandings(seasonId, { clear_existing: true });
      const createdSeries = response?.data?.series_created ?? 0;
      const createdMatches = response?.data?.matches_created ?? 0;
      setMessage(`Utworzono playoffy: serie ${createdSeries}, mecze ${createdMatches}.`);
      await loadData();
    });
  };

  const handleGenerateAndFinalizeRegularSeason = async () => {
    await withAction('auto-regular', async () => {
      const { payload, errorMessage } = validateRulesPayload();
      if (errorMessage) {
        throw new Error(errorMessage);
      }
      await updateSeasonRules(seasonId, payload);

      const seasonResponse = await fetchSeasonById(seasonId);
      const seasonYear = seasonResponse?.data?.year;
      if (!seasonYear) {
        throw new Error('Nie udało się ustalić roku sezonu.');
      }

      const previewResponse = await generateSchedulePreview({
        year: seasonYear,
        num_legs: payload.regular_num_legs,
      });
      await finalizeSchedule(previewResponse.data);
      await cleanUpSchedules();

      setMessage('Wygenerowano i sfinalizowano terminarz regular season.');
      await loadData();
    });
  };

  const handleGeneratePredefinedPlayoffs = async () => {
    await withAction('predefined', async () => {
      if (!predefinedStartDate) {
        throw new Error('Podaj datę startu playoff.');
      }
      await createPredefinedPlayoffs({
        season_id: Number(seasonId),
        start_date: predefinedStartDate,
      });
      setMessage('Wygenerowano pełną drabinkę playoff.');
      await loadData();
    });
  };

  const handleProgressPlayoffs = async () => {
    await withAction('progress', async () => {
      const response = await progressSeasonPlayoffs(seasonId);
      const roundsCreated = response?.data?.rounds_created ?? 0;
      const phaseChangedTo = response?.data?.phase_changed_to;
      setMessage(
        phaseChangedTo
          ? `Progress playoff wykonany. Sezon przeszedł do ${phaseChangedTo}.`
          : `Progress playoff wykonany. Utworzono rund: ${roundsCreated}.`
      );
      await loadData();
    });
  };

  const canTransitionTo = (toPhase) =>
    (ALLOWED_TRANSITIONS[rules.workflow_phase] || []).includes(toPhase);

  const isBusy = useMemo(() => busyAction.length > 0, [busyAction]);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reguły Sezonu
      </Typography>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Konfiguracja
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Aktualna faza: <strong>{rules.workflow_phase}</strong>
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <TextField
            label="Liczba rund (legs)"
            type="number"
            value={rules.regular_num_legs}
            onChange={(e) => handleChange('regular_num_legs', e.target.value)}
          />
          <TextField
            label="Punkty za wygraną"
            type="number"
            value={rules.points_for_win}
            onChange={(e) => handleChange('points_for_win', e.target.value)}
          />
          <TextField
            label="Punkty za porażkę"
            type="number"
            value={rules.points_for_loss}
            onChange={(e) => handleChange('points_for_loss', e.target.value)}
          />
          <TextField
            label="Liczba drużyn w playoff"
            type="number"
            value={rules.playoff_teams_count}
            onChange={(e) => handleChange('playoff_teams_count', e.target.value)}
            helperText="Obsługiwane: 2, 4, 8"
          />
          <TextField
            label="Start regular season"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={rules.regular_season_start}
            onChange={(e) => handleChange('regular_season_start', e.target.value)}
          />
          <TextField
            label="Koniec regular season"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={rules.regular_season_end}
            onChange={(e) => handleChange('regular_season_end', e.target.value)}
          />
          <TextField
            label="Start playoff"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={rules.playoffs_start}
            onChange={(e) => {
              const value = e.target.value;
              handleChange('playoffs_start', value);
              if (!predefinedStartDate) {
                setPredefinedStartDate(value);
              }
            }}
          />
          <TextField
            label="Koniec playoff"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={rules.playoffs_end}
            onChange={(e) => handleChange('playoffs_end', e.target.value)}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Format playoff</Typography>
            <Select
              fullWidth
              value={rules.playoff_format}
              onChange={(e) => handleChange('playoff_format', e.target.value)}
            >
              <MenuItem value="TWO_LEG_KNOCKOUT">TWO_LEG_KNOCKOUT</MenuItem>
              <MenuItem value="SINGLE_ELIMINATION">SINGLE_ELIMINATION</MenuItem>
            </Select>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
          <TextField
            label="Tiebreakers (comma-separated)"
            value={tiebreakersText}
            onChange={(e) => setTiebreakersText(e.target.value)}
            fullWidth
          />
          <TextField
            label="Metadata (JSON)"
            value={metadataText}
            onChange={(e) => setMetadataText(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Checkbox checked={!!rules.playoff_enabled} onChange={(e) => handleChange('playoff_enabled', e.target.checked)} />}
            label="Playoff enabled"
          />
          <FormControlLabel
            control={<Checkbox checked={!!rules.auto_seed_playoffs} onChange={(e) => handleChange('auto_seed_playoffs', e.target.checked)} />}
            label="Auto seed playoffs"
          />
          <FormControlLabel
            control={<Checkbox checked={!!rules.is_locked} onChange={(e) => handleChange('is_locked', e.target.checked)} />}
            label="Zablokuj konfigurację"
          />
        </Box>

        <Button variant="contained" sx={{ mt: 2 }} onClick={handleSave} disabled={isBusy}>
          {busyAction === 'save' ? <CircularProgress size={18} color="inherit" /> : 'Zapisz reguły'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Workflow sezonu
        </Typography>

        {/* Current phase indicator */}
        <Box sx={{ mb: 3 }}>
          <Stepper
            activeStep={WORKFLOW_STEPS.indexOf(rules.workflow_phase)}
            alternativeLabel
          >
            {WORKFLOW_STEPS.map((step) => (
              <Step key={step} completed={WORKFLOW_STEPS.indexOf(step) < WORKFLOW_STEPS.indexOf(rules.workflow_phase)}>
                <StepLabel>{WORKFLOW_STEP_LABELS[step]}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Phase-specific guidance and actions */}
        {rules.workflow_phase === 'DRAFT' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sezon jest w fazie <strong>Draft</strong>. Ustaw reguły powyżej, a potem przejdź do fazy Regular Season.
          </Alert>
        )}
        {rules.workflow_phase === 'REGULAR_SEASON' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Sezon jest w fazie <strong>Regular Season</strong>. Po zakończeniu sezonu zasadniczego, przejdź do fazy Playoffs.
          </Alert>
        )}
        {rules.workflow_phase === 'PLAYOFFS' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Sezon jest w fazie <strong>Playoffs</strong>. Możesz seedować drabinkę z tabeli lub zarządzać meczami na stronie <em>Drabinka Playoff</em>.
          </Alert>
        )}
        {rules.workflow_phase === 'COMPLETED' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sezon jest <strong>zakończony</strong>. Możesz go zarchiwizować.
          </Alert>
        )}
        {rules.workflow_phase === 'ARCHIVED' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sezon jest <strong>zarchiwizowany</strong>. Brak dalszych akcji.
          </Alert>
        )}

        {/* Phase transition buttons */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          Zmiana fazy
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {PHASE_ACTIONS.filter((action) => canTransitionTo(action.to_phase)).map((action) => (
            <Button
              key={action.to_phase}
              variant="contained"
              color="primary"
              onClick={() => handleTransition(action.to_phase)}
              disabled={isBusy}
              size="large"
            >
              {busyAction === `transition-${action.to_phase}` ? <CircularProgress size={18} color="inherit" /> : action.label}
            </Button>
          ))}
          {PHASE_ACTIONS.filter((action) => canTransitionTo(action.to_phase)).length === 0 && (
            <Typography variant="body2" color="text.secondary">Brak dostępnych przejść z tej fazy.</Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Full bracket generation — primary action */}
        <Typography variant="subtitle2" gutterBottom>
          Generuj pełną drabinkę (Play-off + Play-out)
          {!rules.playoff_enabled && (
            <Chip label="Playoff wyłączony" size="small" color="default" sx={{ ml: 1 }} />
          )}
        </Typography>

        {!['REGULAR_SEASON', 'PLAYOFFS'].includes(rules.workflow_phase) && (
          <Alert severity="warning" sx={{ mb: 2 }} variant="outlined">
            Aby wygenerować drabinkę, sezon musi być w fazie <strong>Regular Season</strong> lub <strong>Playoffs</strong>.
            {rules.workflow_phase === 'DRAFT' && ' Najpierw przejdź: Draft → Regular Season.'}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
          Tworzy kompletną strukturę: <strong>Ćwierćfinały (1v8, 2v7, 3v6, 4v5)</strong>, Półfinały, Finał, Mecz o 3. miejsce,
          Mecze o miejsca 5-8 oraz <strong>Play-out (9v12, 10v11)</strong>. Drużyny przypisane z aktualnej tabeli.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            label="Data startu playoff"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={predefinedStartDate}
            onChange={(e) => setPredefinedStartDate(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleGeneratePredefinedPlayoffs}
            disabled={isBusy || !rules.playoff_enabled || !['REGULAR_SEASON', 'PLAYOFFS'].includes(rules.workflow_phase)}
          >
            {busyAction === 'predefined' ? <CircularProgress size={18} color="inherit" /> : 'Generuj pełną drabinkę'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Advanced / alternative actions */}
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Zaawansowane
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={handleSeedPlayoffs}
            disabled={isBusy || !rules.playoff_enabled || !['PLAYOFFS', 'COMPLETED'].includes(rules.workflow_phase)}
          >
            {busyAction === 'seed' ? <CircularProgress size={18} color="inherit" /> : 'Seed tylko QF (bez play-out)'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            onClick={handleProgressPlayoffs}
            disabled={isBusy || !['PLAYOFFS', 'COMPLETED'].includes(rules.workflow_phase)}
          >
            {busyAction === 'progress' ? <CircularProgress size={18} color="inherit" /> : 'Progress playoff'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleGenerateAndFinalizeRegularSeason}
            disabled={isBusy}
          >
            {busyAction === 'auto-regular' ? <CircularProgress size={18} color="inherit" /> : 'Auto: generuj + finalizuj regular season'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Historia zmian workflow
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {events.length === 0 ? (
          <Typography variant="body2">Brak zdarzeń workflow.</Typography>
        ) : (
          events.map((event) => (
            <Box key={event.id} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{new Date(event.changed_at).toLocaleString()}</strong>: {event.from_phase} -> {event.to_phase}
              </Typography>
              {event.note && <Typography variant="caption">{event.note}</Typography>}
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default SeasonRulesPage;
